import { Chip } from '@mui/material';
import { format } from 'date-fns';
import { CreatedBy } from 'components/common/CreatedBy/CreatedBy';
import { DataTable, DataTableProps } from 'components/data/DataTable/DataTable';
import { useEffect, useState } from 'react';
import {
    useLazyGetIssueTicketsByDeviceIdQuery,
    useLazyGetResolvedIssueTicketsByDeviceIdQuery,
} from 'store/services/issue-tickets';
import { Device } from 'types';
import { Ticket } from 'types/User/firebaseTypes/ticket';
import { formatDeviceTickets } from '../helpers';
import {
    convertDeliveryStatusToDisplayName,
    convertDeliveryStatusToStatusColor,
    ticketTriggerToDisplayName,
} from 'lib/constants';
import { IssueCauseIcon } from 'components/common/IssueCauseIcon/IssueCauseIcon';
import { GridRowParams } from '@mui/x-data-grid';
import { IssueDetailsModal } from 'views/Issues/IssueDetailsModal/IssueDetailsModal';
import styles from './DeviceIssues.module.scss';

export interface DeviceIssuesProps {
    device: Device;
}

export const DeviceIssues = ({ device }: DeviceIssuesProps) => {
    const [tableData, setTableData] = useState<Record<string, any>[] | null>(
        null
    );
    const [loading, setLoading] = useState(true);
    const [isDetailsModalOpen, setIsDetailsModalOpen] =
        useState<boolean>(false);
    const [activeTicket, setActiveTicket] = useState<Ticket | null>(null);
    const [didUpdateIssue, setDidUpdateIssue] = useState(false);

    const openDetailsModal = (params: GridRowParams) => {
        setActiveTicket(params.row);
        setIsDetailsModalOpen(true);
    };

    const supplierId = device.protected?.supplierId;

    const [getActiveTicketsQuery] = useLazyGetIssueTicketsByDeviceIdQuery();
    const [getResolvedTicketsQuery] =
        useLazyGetResolvedIssueTicketsByDeviceIdQuery();

    const resetVariables = () => {
        setLoading(false);
        setDidUpdateIssue(false);
    };

    useEffect(() => {
        if (supplierId !== undefined) {
            Promise.all([
                getActiveTicketsQuery({
                    supplierId,
                    deviceId: device.deviceId,
                }),
                getResolvedTicketsQuery({
                    supplierId,
                    deviceId: device.deviceId,
                }),
            ]).then(async (result) => {
                if (result) {
                    let ticketsList: Ticket[] = [];

                    if (result[0]?.data) {
                        ticketsList = [...formatDeviceTickets(result[0]?.data)];
                    }

                    if (result[1]?.data) {
                        ticketsList = [
                            ...ticketsList,
                            ...formatDeviceTickets(result[1]?.data),
                        ];
                    }

                    if (!ticketsList.length) {
                        return setLoading(false);
                    }

                    ticketsList.filter(
                        (ticket) => ticket.ticketCategory !== 'delivery'
                    );

                    await Promise.all(ticketsList).then((result) => {
                        setTableData(result);
                    });

                    resetVariables();
                } else {
                    resetVariables();
                }
            });
        }
    }, [
        device.deviceId,
        supplierId,
        getActiveTicketsQuery,
        getResolvedTicketsQuery,
        didUpdateIssue,
    ]);

    return (
        <div className={styles.container}>
            <DataTable
                id="DeviceTickets"
                data={tableData}
                columns={deviceTicketColumns}
                loading={loading}
                getRowCount={(data) => data?.length ?? 0}
                onRowClick={openDetailsModal}
                dataFormatter={(data) => data}
                csvOptions={{
                    fileName: `issue-history-${device.shortDeviceId}-${device.customer_name}`,
                }}
            />
            {activeTicket && (
                <IssueDetailsModal
                    modalOpen={isDetailsModalOpen}
                    setModalOpen={setIsDetailsModalOpen}
                    ticket={activeTicket}
                    setActiveTicket={setActiveTicket}
                    onIssueUpdated={setDidUpdateIssue}
                />
            )}
        </div>
    );
};

const deviceTicketColumns: DataTableProps['columns'] = [
    {
        headerName: 'Date Created',
        field: 'dateCreated_iso',
        fbKey: 'dateCreated_iso',
        valueFormatter: (param) => {
            if (param.value) return format(new Date(param.value), 'yyyy/MM/dd');
        },
    },
    {
        headerName: 'Status',
        field: 'status',
        valueFormatter: (param) => {
            return convertDeliveryStatusToDisplayName[param.value];
        },
        renderCell: (params) => {
            // Issue tickets and delivery tickets current have the same set of statuses. If those ever diverge, we will need
            // To create separate `convertDeliveryStatusToStatusColor` and `convertDeliveryStatusToDisplayName` data.
            return (
                <Chip
                    sx={{
                        backgroundColor:
                            convertDeliveryStatusToStatusColor[params.value],
                    }}
                    label={convertDeliveryStatusToDisplayName[params.value]}
                />
            );
        },
    },
    {
        headerName: 'Ticket Name',
        field: 'name',
        fbKey: 'name',
    },
    {
        headerName: 'Assigned User',
        field: 'taggedUsers',
        fbKey: 'taggedUsers',
        renderCell: (params) => {
            if (params?.value && params.value[0]?.email)
                return <Chip label={params.value[0]?.email} />;
            else return <Chip color="warning" label="Unassigned" />;
        },
        valueFormatter: (param) => {
            if (param.value && param.value[0]?.email)
                return param.value[0]?.email;
            return 'Unassigned';
        },
    },
    {
        headerName: 'Cause',
        field: 'ticketTrigger',
        renderCell: (params) => {
            return <IssueCauseIcon trigger={params.value} />;
        },
        valueFormatter: (param) => ticketTriggerToDisplayName[param.value],
    },
    {
        headerName: 'Created By',
        field: 'createdBy',
        renderCell: (params) => {
            return <CreatedBy ticket={params.row} showIcon={false} />;
        },
    },
];
